import * as React from "react";
import { cn } from "../../../lib/utils";
import {
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  AlertTriangle,
  AlertOctagon,
  Info,
  ChevronDown,
  ChevronUp,
  Filter,
  Lightbulb,
  Clock,
  Target,
} from "lucide-react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { Badge } from "../../badge";
import { Button } from "../../button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../select";

// Types matching Rust CloudMisconfigResult
type Severity = "critical" | "high" | "medium" | "low" | "info";
type CloudProvider = "aws" | "gcp" | "azure" | "digitalocean" | "firebase";

interface MisconfigFinding {
  asset_name: string;
  provider: CloudProvider;
  check_id: string;
  severity: Severity;
  title: string;
  description: string;
  evidence: Record<string, unknown>;
  remediation: string;
}

interface MisconfigStats {
  checks_performed: number;
  duration_ms: number;
  by_severity: Record<string, number>;
}

export interface CloudMisconfigModuleData {
  target?: string;
  assets_analyzed?: number;
  findings?: MisconfigFinding[];
  score?: number;
  stats?: MisconfigStats;
}

export interface CloudMisconfigModuleViewProps {
  data: CloudMisconfigModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  hideHeader?: boolean;
}

// Severity configuration
const SEVERITY_CONFIG: Record<Severity, {
  label: string;
  icon: typeof AlertOctagon;
  color: string;
  bgColor: string;
  borderColor: string;
}> = {
  critical: {
    label: "Critical",
    icon: AlertOctagon,
    color: "text-red-400",
    bgColor: "bg-red-500/15",
    borderColor: "border-red-500/40",
  },
  high: {
    label: "High",
    icon: ShieldX,
    color: "text-orange-400",
    bgColor: "bg-orange-500/15",
    borderColor: "border-orange-500/40",
  },
  medium: {
    label: "Medium",
    icon: AlertTriangle,
    color: "text-amber-400",
    bgColor: "bg-amber-500/15",
    borderColor: "border-amber-500/40",
  },
  low: {
    label: "Low",
    icon: ShieldCheck,
    color: "text-cyan-400",
    bgColor: "bg-cyan-500/15",
    borderColor: "border-cyan-500/40",
  },
  info: {
    label: "Info",
    icon: Info,
    color: "text-slate-400",
    bgColor: "bg-slate-500/15",
    borderColor: "border-slate-500/40",
  },
};

// Provider labels
const PROVIDER_LABELS: Record<CloudProvider, string> = {
  aws: "AWS S3",
  gcp: "Google Cloud",
  azure: "Azure Blob",
  digitalocean: "DigitalOcean",
  firebase: "Firebase",
};

// Severity badge component
function SeverityBadge({ severity }: { severity: Severity }) {
  const config = SEVERITY_CONFIG[severity];
  const Icon = config.icon;

  return (
    <Badge
      variant="outline"
      className={cn(
        "text-xs gap-1 font-medium",
        config.bgColor,
        config.color,
        config.borderColor
      )}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

// Individual finding card
function FindingCard({ finding, defaultExpanded = false }: { finding: MisconfigFinding; defaultExpanded?: boolean }) {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  const config = SEVERITY_CONFIG[finding.severity];
  const Icon = config.icon;

  return (
    <div
      className={cn(
        "border rounded-lg overflow-hidden transition-all",
        config.borderColor,
        config.bgColor
      )}
    >
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-start justify-between p-4 text-left hover:bg-muted/20 transition-colors"
      >
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Icon className={cn("h-5 w-5 mt-0.5 flex-shrink-0", config.color)} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <SeverityBadge severity={finding.severity} />
              <Badge variant="outline" className="text-xs text-muted-foreground">
                {PROVIDER_LABELS[finding.provider] || finding.provider}
              </Badge>
            </div>
            <h4 className="font-medium text-sm text-foreground">{finding.title}</h4>
            <p className="text-xs text-muted-foreground font-mono mt-1">
              {finding.asset_name}
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-border/50">
          {/* Description */}
          <div className="pt-4">
            <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Description
            </h5>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {finding.description}
            </p>
          </div>

          {/* Evidence */}
          {finding.evidence && Object.keys(finding.evidence).length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Evidence
              </h5>
              <div className="bg-muted/30 rounded-md p-3 overflow-x-auto">
                <pre className="text-xs font-mono text-foreground">
                  {JSON.stringify(finding.evidence, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Remediation */}
          <div className="flex items-start gap-2 p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
            <Lightbulb className="h-4 w-4 text-cyan-400 mt-0.5 flex-shrink-0" />
            <div>
              <h5 className="text-xs font-medium text-cyan-400 uppercase tracking-wider mb-1">
                Remediation
              </h5>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {finding.remediation}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Score display component
function ScoreDisplay({ score }: { score: number }) {
  let color = "text-emerald-400";
  let label = "Excellent";

  if (score < 50) {
    color = "text-red-400";
    label = "Critical";
  } else if (score < 70) {
    color = "text-orange-400";
    label = "Poor";
  } else if (score < 85) {
    color = "text-amber-400";
    label = "Fair";
  } else if (score < 95) {
    color = "text-cyan-400";
    label = "Good";
  }

  return (
    <div className="text-center">
      <div className={cn("text-4xl font-bold font-mono", color)}>
        {score}
      </div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

export function CloudMisconfigModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: CloudMisconfigModuleViewProps) {
  const [severityFilter, setSeverityFilter] = React.useState<Severity | "all">("all");

  const findings = data.findings || [];
  const stats = data.stats;

  // Count findings by severity
  const severityCounts = React.useMemo(() => {
    const counts: Record<Severity, number> = {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      info: 0,
    };
    for (const finding of findings) {
      counts[finding.severity]++;
    }
    return counts;
  }, [findings]);

  // Filter findings
  const filteredFindings = React.useMemo(() => {
    if (severityFilter === "all") return findings;
    return findings.filter((f) => f.severity === severityFilter);
  }, [findings, severityFilter]);

  // Sort by severity (critical first)
  const sortedFindings = React.useMemo(() => {
    const severityOrder: Severity[] = ["critical", "high", "medium", "low", "info"];
    return [...filteredFindings].sort(
      (a, b) => severityOrder.indexOf(a.severity) - severityOrder.indexOf(b.severity)
    );
  }, [filteredFindings]);

  const hasCritical = severityCounts.critical > 0;
  const hasHigh = severityCounts.high > 0;

  return (
    <ModuleContainer
      moduleName="cloud_misconfig"
      icon={ShieldAlert}
      data={data}
      isLoading={isLoading}
      error={error}
      score={data.score}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className={cn("bg-card/50", hasCritical && "border-red-500/30")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <AlertOctagon className="h-3 w-3 text-red-400" />
                Critical
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={cn(
                "text-2xl font-bold font-mono",
                severityCounts.critical > 0 ? "text-red-400" : "text-emerald-400"
              )}>
                {severityCounts.critical}
              </p>
            </CardContent>
          </Card>

          <Card className={cn("bg-card/50", hasHigh && "border-orange-500/30")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <ShieldX className="h-3 w-3 text-orange-400" />
                High
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={cn(
                "text-2xl font-bold font-mono",
                severityCounts.high > 0 ? "text-orange-400" : "text-emerald-400"
              )}>
                {severityCounts.high}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 text-amber-400" />
                Medium
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">
                {severityCounts.medium}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <Target className="h-3 w-3 text-cyan-400" />
                Assets Scanned
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono text-cyan-400">
                {data.assets_analyzed || 0}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Critical Alert */}
        {hasCritical && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 animate-pulse">
            <div className="flex items-center gap-3">
              <AlertOctagon className="h-6 w-6 text-red-400" />
              <div>
                <h4 className="font-semibold text-red-400">Critical Misconfigurations Found</h4>
                <p className="text-sm text-muted-foreground">
                  {severityCounts.critical} critical issue(s) require immediate attention.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Score and Stats */}
        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border">
          <div className="flex items-center gap-6">
            {data.score !== undefined && <ScoreDisplay score={data.score} />}
            <div className="h-12 w-px bg-border" />
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Target className="h-4 w-4" />
                <span>{data.assets_analyzed || 0} assets analyzed</span>
              </div>
              {stats && (
                <>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <ShieldAlert className="h-4 w-4" />
                    <span>{stats.checks_performed} checks performed</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>{(stats.duration_ms / 1000).toFixed(1)}s scan time</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Findings List */}
        {findings.length > 0 && (
          <ModuleSection
            title="Security Findings"
            icon={ShieldAlert}
            badge={
              <Badge variant="outline" className="text-xs">
                {findings.length}
              </Badge>
            }
          >
            {/* Filter Controls */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Filter className="h-4 w-4" />
                <span>Filter by severity:</span>
              </div>
              <Select
                value={severityFilter}
                onValueChange={(v) => setSeverityFilter(v as Severity | "all")}
              >
                <SelectTrigger className="w-[140px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ({findings.length})</SelectItem>
                  <SelectItem value="critical">Critical ({severityCounts.critical})</SelectItem>
                  <SelectItem value="high">High ({severityCounts.high})</SelectItem>
                  <SelectItem value="medium">Medium ({severityCounts.medium})</SelectItem>
                  <SelectItem value="low">Low ({severityCounts.low})</SelectItem>
                  <SelectItem value="info">Info ({severityCounts.info})</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Findings */}
            <div className="space-y-3">
              {sortedFindings.map((finding, index) => (
                <FindingCard
                  key={`${finding.check_id}-${finding.asset_name}-${index}`}
                  finding={finding}
                  defaultExpanded={finding.severity === "critical"}
                />
              ))}
            </div>

            {sortedFindings.length === 0 && severityFilter !== "all" && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No findings with {severityFilter} severity</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSeverityFilter("all")}
                  className="mt-2"
                >
                  Clear filter
                </Button>
              </div>
            )}
          </ModuleSection>
        )}

        {/* No Findings State */}
        {findings.length === 0 && (data.assets_analyzed || 0) > 0 && (
          <div className="text-center py-8 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
            <ShieldCheck className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-emerald-400">No Misconfigurations Found</h3>
            <p className="text-sm text-muted-foreground mt-1">
              All {data.assets_analyzed} analyzed assets passed security checks.
            </p>
          </div>
        )}

        {/* No Assets State */}
        {(data.assets_analyzed || 0) === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <h3 className="text-lg font-semibold">No Cloud Assets Found</h3>
            <p className="text-sm mt-1">
              No cloud storage assets were discovered for this target.
            </p>
          </div>
        )}
      </div>
    </ModuleContainer>
  );
}

CloudMisconfigModuleView.displayName = "CloudMisconfigModuleView";
